import { Bell, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { PageHeader, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { EmptyState } from "@/components/data/empty-state";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { useNavegacao } from "@/lib/navegacao";
import { useDefinirMeuAviso, useMeusAvisos } from "@/hooks/use-avisos";

// Meus avisos — o que ESTA pessoa recebe pelo WhatsApp do Rizoma, por categoria.
// No menu da conta, ao lado de "Trocar minha senha", para qualquer papel. Tudo
// nasce ligado para quem tem telefone; só aparecem as categorias que têm aviso
// para o lado da conta (produtor ou comprador).

export default function ContaAvisos() {
  const consulta = useMeusAvisos();
  const definir = useDefinirMeuAviso();
  const { abrir } = useNavegacao();
  const { toast } = useToast();
  const aguardando = estadoDaConsulta(consulta, { variante: "painel" });

  const dados = consulta.data;
  const trocarNumero = dados?.podeTrocarNumero
    ? <Button variant="outline" onClick={() => abrir("/admin/usuarios")} className={ALVO_TOQUE_BOTAO} data-testid="button-trocar-numero"><Phone className="h-4 w-4 mr-2" />Trocar número</Button>
    : undefined;

  return (
    <div className="space-y-5 max-w-3xl">
      <PageHeader
        titulo="Meus avisos"
        descricao={dados?.telefone
          ? `O que o Rizoma avisa pelo WhatsApp. Chega no número ${dados.telefone}.`
          : "O que o Rizoma avisa pelo WhatsApp."}
        acao={trocarNumero}
      />
      {aguardando ?? (
        !dados!.telefone ? (
          <EmptyState
            icone={Phone}
            titulo="Sem telefone na sua conta"
            descricao={dados!.podeTrocarNumero
              ? "Cadastre o seu número em Usuários para receber os avisos pelo WhatsApp."
              : "Peça a quem administra a sua conta para cadastrar o seu número. Sem ele, os avisos não têm para onde ir."}
            acao={trocarNumero}
            testId="empty-sem-telefone"
          />
        ) : dados!.categorias.length === 0 ? (
          <EmptyState icone={Bell} titulo="Nenhum aviso para a sua conta" descricao="Os avisos do WhatsApp são do Mercado. Quando houver algum para você, ele aparece aqui." testId="empty-sem-avisos" />
        ) : (
          <div className="rounded-lg border bg-card">
            <div className="px-4">
              {dados!.categorias.map(c => (
                <div key={c.categoria} className="flex items-start justify-between gap-4 py-3.5 border-t first:border-t-0" data-testid={`pref-${c.categoria}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold">{c.rotulo}</p>
                    <p className="text-xs text-muted-foreground">{c.descricao}</p>
                    {!c.ativo && <p className="mt-1 text-xs text-atencao" data-testid={`aviso-desligado-${c.categoria}`}>{c.aoDesligar}</p>}
                  </div>
                  <Switch
                    checked={c.ativo}
                    disabled={definir.isPending}
                    aria-label={c.rotulo}
                    data-testid={`switch-${c.categoria}`}
                    onCheckedChange={ativo => definir.mutate({ categoria: c.categoria, ativo }, {
                      onError: e => toast({ title: "Não deu para salvar", description: e.message, variant: "destructive" }),
                    })}
                  />
                </div>
              ))}
            </div>
            <p className="border-t bg-muted/40 px-4 py-3 text-xs text-muted-foreground rounded-b-lg">
              Os avisos saem do número do Rizoma e não são lidos se você responder. Para falar com a outra pessoa, use o WhatsApp de vocês.
            </p>
          </div>
        )
      )}
    </div>
  );
}
